<template>
  <div class="app-container">
    <!-- Header Section -->
    <div class="header-section">
      <h2 class="page-title">Prośby o dostęp do CRM</h2>
      <p class="page-subtitle">Przeglądaj i zarządzaj prośbami użytkowników o wczesny dostęp do CRM</p>
    </div>

    <!-- Statistics Cards -->
    <el-row :gutter="20" class="stats-cards">
      <el-col :xs="24" :sm="8" :md="8">
        <el-card class="stat-card stat-pending">
          <div class="stat-content">
            <div class="stat-number">{{ stats.pending }}</div>
            <div class="stat-label">Oczekujące</div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="8" :md="8">
        <el-card class="stat-card stat-approved">
          <div class="stat-content">
            <div class="stat-number">{{ stats.approved }}</div>
            <div class="stat-label">Zatwierdzone</div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="8" :md="8">
        <el-card class="stat-card stat-rejected">
          <div class="stat-content">
            <div class="stat-number">{{ stats.rejected }}</div>
            <div class="stat-label">Odrzucone</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- Filter Tabs -->
    <el-card class="requests-card">
      <div slot="header" class="card-header">
        <div class="card-header-left">
          <h3>Lista zgłoszeń</h3>
        </div>
        <div class="card-header-right">
          <el-radio-group v-model="statusFilter" size="small" @change="loadRequests">
            <el-radio-button label="">Wszystkie</el-radio-button>
            <el-radio-button label="pending">Oczekujące</el-radio-button>
            <el-radio-button label="approved">Zatwierdzone</el-radio-button>
            <el-radio-button label="rejected">Odrzucone</el-radio-button>
          </el-radio-group>
        </div>
      </div>

      <!-- Table -->
      <el-table
        v-loading="loading"
        :data="requests"
        border
        style="width: 100%; cursor: pointer;"
        empty-text="Brak zgłoszeń"
        @row-click="handleViewDetails"
      >
        <el-table-column label="Użytkownik" min-width="200">
          <template slot-scope="scope">
            <div class="user-cell">
              <div class="user-name">{{ scope.row.first_name }} {{ scope.row.last_name }}</div>
              <div class="user-email">{{ scope.row.email }}</div>
              <div v-if="scope.row.company" class="user-company">{{ scope.row.company }}</div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Telefon" width="140">
          <template slot-scope="scope">
            {{ scope.row.phone }}
          </template>
        </el-table-column>

        <el-table-column label="Rola" width="160">
          <template slot-scope="scope">
            <el-tag size="mini" type="info">{{ getRoleLabel(scope.row.role) }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Oferty" width="100" align="center">
          <template slot-scope="scope">
            {{ scope.row.properties_count }}
          </template>
        </el-table-column>

        <el-table-column label="Status" width="140" align="center">
          <template slot-scope="scope">
            <el-tag
              :type="getStatusType(scope.row.status)"
              size="small"
            >
              <i :class="getStatusIcon(scope.row.status)"></i>
              {{ getStatusLabel(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Data zgłoszenia" width="150" align="center">
          <template slot-scope="scope">
            {{ formatDate(scope.row.created_at) }}
          </template>
        </el-table-column>

        <el-table-column label="Akcje" width="300" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button
              type="primary"
              size="small"
              icon="el-icon-view"
              @click.stop="handleViewDetails(scope.row)"
            >
              Szczegóły
            </el-button>
            <el-button
              v-if="scope.row.status === 'pending'"
              type="success"
              size="small"
              icon="el-icon-check"
              @click.stop="handleApprove(scope.row)"
            >
              Zatwierdź
            </el-button>
            <el-button
              v-if="scope.row.status === 'pending'"
              type="danger"
              size="small"
              icon="el-icon-close"
              @click.stop="handleReject(scope.row)"
            >
              Odrzuć
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Details Dialog -->
    <el-dialog
      :visible.sync="detailsVisible"
      title="Szczegóły zgłoszenia CRM"
      width="600px"
    >
      <div v-if="selectedRequest">
        <el-descriptions :column="1" border size="medium">
          <el-descriptions-item label="Imię i nazwisko">
            {{ selectedRequest.first_name }} {{ selectedRequest.last_name }}
          </el-descriptions-item>
          <el-descriptions-item label="Email">
            {{ selectedRequest.email }}
          </el-descriptions-item>
          <el-descriptions-item label="Telefon">
            {{ selectedRequest.phone }}
          </el-descriptions-item>
          <el-descriptions-item label="Firma">
            {{ selectedRequest.company || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="Rola">
            {{ getRoleLabel(selectedRequest.role) }}
          </el-descriptions-item>
          <el-descriptions-item label="Liczba ofert">
            {{ selectedRequest.properties_count }}
          </el-descriptions-item>
          <el-descriptions-item label="Priorytety">
            {{ selectedRequest.priorities || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="Status">
            <el-tag :type="getStatusType(selectedRequest.status)" size="small">
              {{ getStatusLabel(selectedRequest.status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item v-if="selectedRequest.rejection_reason" label="Powód odrzucenia">
            <span style="color: #F56C6C;">{{ selectedRequest.rejection_reason }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="Data zgłoszenia">
            {{ formatDate(selectedRequest.created_at) }}
          </el-descriptions-item>
          <el-descriptions-item v-if="selectedRequest.reviewed_at" label="Data rozpatrzenia">
            {{ formatDate(selectedRequest.reviewed_at) }}
          </el-descriptions-item>
          <el-descriptions-item v-if="selectedRequest.reviewer" label="Rozpatrzył">
            {{ selectedRequest.reviewer.email }}
          </el-descriptions-item>
        </el-descriptions>
      </div>
      <span slot="footer">
        <el-button @click="detailsVisible = false">Zamknij</el-button>
        <el-button
          v-if="selectedRequest && selectedRequest.status === 'pending'"
          type="success"
          icon="el-icon-check"
          @click="handleApproveFromModal"
        >
          Zatwierdź
        </el-button>
        <el-button
          v-if="selectedRequest && selectedRequest.status === 'pending'"
          type="danger"
          icon="el-icon-close"
          @click="handleRejectFromModal"
        >
          Odrzuć
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getCrmRequests, approveCrmRequest, rejectCrmRequest } from '@/api/crm';

export default {
  name: 'CrmRequests',
  data() {
    return {
      loading: false,
      requests: [],
      statusFilter: '',
      stats: {
        pending: 0,
        approved: 0,
        rejected: 0,
      },
      detailsVisible: false,
      selectedRequest: null,
    };
  },
  created() {
    this.loadRequests();
  },
  methods: {
    async loadRequests() {
      try {
        this.loading = true;
        const params = {};
        if (this.statusFilter) {
          params.status = this.statusFilter;
        }
        const result = await getCrmRequests(params);
        this.requests = result.data || [];
        if (result.counts) {
          this.stats.pending = result.counts.pending || 0;
          this.stats.approved = result.counts.approved || 0;
          this.stats.rejected = result.counts.rejected || 0;
        }
      } catch (error) {
        console.error('Error loading CRM requests:', error);
        this.$message.error('Błąd podczas ładowania zgłoszeń');
      } finally {
        this.loading = false;
      }
    },

    async handleApprove(request) {
      try {
        await this.$confirm(
          `Czy na pewno chcesz zatwierdzić dostęp do CRM dla ${request.first_name} ${request.last_name}?`,
          'Potwierdzenie',
          {
            confirmButtonText: 'Tak, zatwierdź',
            cancelButtonText: 'Anuluj',
            type: 'success',
          }
        );

        this.loading = true;
        await approveCrmRequest(request.id);
        this.$message.success('Dostęp do CRM został zatwierdzony');
        await this.loadRequests();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error approving CRM request:', error);
          this.$message.error('Błąd podczas zatwierdzania');
        }
      } finally {
        this.loading = false;
      }
    },

    async handleReject(request) {
      try {
        const { value: reason } = await this.$prompt(
          'Podaj powód odrzucenia',
          'Odrzuć prośbę o CRM',
          {
            confirmButtonText: 'Odrzuć',
            cancelButtonText: 'Anuluj',
            inputPlaceholder: 'Wpisz powód odrzucenia...',
            inputValidator: (value) => {
              if (!value || value.trim() === '') {
                return 'Musisz podać powód odrzucenia';
              }
              return true;
            },
          }
        );

        this.loading = true;
        await rejectCrmRequest(request.id, reason);
        this.$message.success('Prośba o CRM została odrzucona');
        await this.loadRequests();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error rejecting CRM request:', error);
          this.$message.error('Błąd podczas odrzucania');
        }
      } finally {
        this.loading = false;
      }
    },

    handleViewDetails(request) {
      this.selectedRequest = request;
      this.detailsVisible = true;
    },

    async handleApproveFromModal() {
      if (!this.selectedRequest) return;
      this.detailsVisible = false;
      await this.handleApprove(this.selectedRequest);
    },

    async handleRejectFromModal() {
      if (!this.selectedRequest) return;
      this.detailsVisible = false;
      await this.handleReject(this.selectedRequest);
    },

    getStatusType(status) {
      const map = {
        pending: 'warning',
        approved: 'success',
        rejected: 'danger',
      };
      return map[status] || 'info';
    },

    getStatusIcon(status) {
      const map = {
        pending: 'el-icon-time',
        approved: 'el-icon-circle-check',
        rejected: 'el-icon-circle-close',
      };
      return map[status] || '';
    },

    getStatusLabel(status) {
      const map = {
        pending: 'Oczekujące',
        approved: 'Zatwierdzone',
        rejected: 'Odrzucone',
      };
      return map[status] || status;
    },

    getRoleLabel(role) {
      const map = {
        agent: 'Agent nieruchomości',
        broker: 'Broker / Manager',
        developer: 'Deweloper',
        investor: 'Inwestor',
        property_manager: 'Zarządca',
        other: 'Inne',
      };
      return map[role] || role;
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>

<style scoped>
.header-section {
  margin-bottom: 20px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 5px 0;
}

.page-subtitle {
  font-size: 14px;
  color: #909399;
  margin: 0;
}

.stats-cards {
  margin-bottom: 20px;
}

.stat-card {
  text-align: center;
  margin-bottom: 10px;
}

.stat-content {
  padding: 10px 0;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
}

.stat-pending .stat-number {
  color: #E6A23C;
}

.stat-approved .stat-number {
  color: #67C23A;
}

.stat-rejected .stat-number {
  color: #F56C6C;
}

.stat-label {
  font-size: 13px;
  color: #909399;
  margin-top: 4px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.card-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.user-cell {
  line-height: 1.4;
}

.user-name {
  font-weight: 600;
  color: #303133;
}

.user-email {
  font-size: 12px;
  color: #909399;
}

.user-company {
  font-size: 12px;
  color: #606266;
  font-style: italic;
}

.requests-card {
  margin-bottom: 20px;
}
</style>
